/**
 * Component Hierarchy Utilities
 *
 * Provides functions for calculating nesting depth, traversing parent-child relationships,
 * and managing component hierarchies in the TerraMatrix editor.
 *
 * Used primarily for:
 * - Depth-based color coding of selected components
 * - Understanding component relationships
 * - Navigation and selection logic
 */

import type { Component } from '@/types';
import { devLog, devWarn } from './devLog';

/**
 * Maximum nesting depth to prevent infinite loops
 * Based on template library analysis: max observed depth is 7 levels
 */
const MAX_DEPTH = 20;

/**
 * Cache for component depth calculations
 * Key: component ID, Value: calculated depth
 * Cleared when component structure changes
 */
const depthCache = new Map<string, number>();

/**
 * Calculate the nesting depth of a component in the hierarchy
 *
 * @param componentId - ID of the component to calculate depth for
 * @param allComponents - Array of all components in the editor
 * @returns Depth level (0 = root/no parent, 1 = direct child of root, etc.)
 *
 * @example
 * ```typescript
 * const depth = getComponentDepth('button-123', components);
 * // Returns: 2 (button is nested 2 levels deep)
 * ```
 */
export function getComponentDepth(
  componentId: string,
  allComponents: Component[]
): number {
  const component = allComponents.find((c) => c.id === componentId);

  // Component not in store yet (during initial render) - treat as root
  if (!component) {
    return 0;
  }

  // Root component (no parent)
  if (!component.parentId) {
    return 0;
  }

  let depth = 0;
  let currentId: string | undefined = component.parentId;
  const visited = new Set<string>(); // Detect circular references

  // Traverse up the parent chain
  while (currentId && depth < MAX_DEPTH) {
    // Circular reference detection
    if (visited.has(currentId)) {
      devWarn('LAYOUT', `Circular reference detected in component hierarchy`, {
        componentId,
        currentId,
        visited: Array.from(visited),
      });
      break;
    }

    visited.add(currentId);

    const parent = allComponents.find((c) => c.id === currentId);

    if (!parent) {
      devWarn('LAYOUT', `Parent component not found: ${currentId}`, {
        childId: componentId,
      });
      break;
    }

    depth++;
    currentId = parent.parentId;
  }

  if (depth >= MAX_DEPTH) {
    devWarn('LAYOUT', `Maximum nesting depth reached`, {
      componentId,
      maxDepth: MAX_DEPTH,
    });
  }

  devLog('LAYOUT', `Component depth calculated`, {
    componentId,
    depth,
    parentChainLength: visited.size,
  });

  return depth;
}

/**
 * Calculate component depth with caching for performance
 *
 * @param componentId - ID of the component
 * @param allComponents - Array of all components
 * @returns Cached depth or newly calculated depth
 *
 * @example
 * ```typescript
 * // First call calculates and caches
 * const depth1 = getComponentDepthCached('btn-1', components);
 *
 * // Second call returns cached value (O(1))
 * const depth2 = getComponentDepthCached('btn-1', components);
 * ```
 */
export function getComponentDepthCached(
  componentId: string,
  allComponents: Component[]
): number {
  // Check cache first
  if (depthCache.has(componentId)) {
    devLog('PERFORMANCE', `Cache hit for component depth`, { componentId });
    return depthCache.get(componentId)!;
  }

  // Calculate and cache
  const depth = getComponentDepth(componentId, allComponents);
  depthCache.set(componentId, depth);

  devLog('PERFORMANCE', `Depth calculated and cached`, { componentId, depth });

  return depth;
}

/**
 * Clear the depth cache
 * Should be called when component structure changes (add, remove, reparent)
 *
 * @example
 * ```typescript
 * // After adding/removing components
 * clearDepthCache();
 * ```
 */
export function clearDepthCache(): void {
  const size = depthCache.size;
  depthCache.clear();
  devLog('PERFORMANCE', `Depth cache cleared`, { previousSize: size });
}

/**
 * Clear depth cache for specific component IDs
 * More granular than clearing entire cache
 *
 * @param componentIds - Array of component IDs to clear from cache
 */
export function clearDepthCacheFor(componentIds: string[]): void {
  componentIds.forEach((id) => {
    depthCache.delete(id);
  });

  devLog('PERFORMANCE', `Selective cache clear`, {
    clearedCount: componentIds.length,
  });
}

/**
 * Get all direct children of a component
 *
 * @param componentId - ID of the parent component
 * @param allComponents - Array of all components
 * @returns Array of child components (direct children only, not descendants)
 *
 * @example
 * ```typescript
 * const children = getComponentChildren('container-1', components);
 * // Returns: [button, image, text] (direct children only)
 * ```
 */
export function getComponentChildren(
  componentId: string,
  allComponents: Component[]
): Component[] {
  const children = allComponents.filter((c) => c.parentId === componentId);

  devLog('LAYOUT', `Found component children`, {
    parentId: componentId,
    childCount: children.length,
    childIds: children.map((c) => c.id),
  });

  return children;
}

/**
 * Get all descendants of a component (children, grandchildren, etc.)
 *
 * @param componentId - ID of the parent component
 * @param allComponents - Array of all components
 * @returns Array of all descendant components
 *
 * @example
 * ```typescript
 * const descendants = getComponentDescendants('section-1', components);
 * // Returns: [container, button, text, image, ...] (all nested components)
 * ```
 */
export function getComponentDescendants(
  componentId: string,
  allComponents: Component[]
): Component[] {
  const descendants: Component[] = [];
  const directChildren = getComponentChildren(componentId, allComponents);

  for (const child of directChildren) {
    descendants.push(child);
    // Recursively get descendants of this child
    const childDescendants = getComponentDescendants(child.id, allComponents);
    descendants.push(...childDescendants);
  }

  devLog('LAYOUT', `Found component descendants`, {
    parentId: componentId,
    descendantCount: descendants.length,
  });

  return descendants;
}

/**
 * Get the full hierarchy path from root to component
 *
 * @param componentId - ID of the component
 * @param allComponents - Array of all components
 * @returns Array of component IDs from root to target (inclusive)
 *
 * @example
 * ```typescript
 * const path = getComponentPath('button-1', components);
 * // Returns: ['page-1', 'section-1', 'container-1', 'button-1']
 * ```
 */
export function getComponentPath(
  componentId: string,
  allComponents: Component[]
): string[] {
  const path: string[] = [];
  let currentId: string | undefined = componentId;
  const visited = new Set<string>();

  while (currentId && visited.size < MAX_DEPTH) {
    // Circular reference detection
    if (visited.has(currentId)) {
      devWarn('LAYOUT', `Circular reference detected in path`, {
        componentId,
        currentId,
      });
      break;
    }

    visited.add(currentId);
    path.unshift(currentId); // Add to beginning of array

    const component = allComponents.find((c) => c.id === currentId);
    currentId = component?.parentId;
  }

  devLog('LAYOUT', `Component path calculated`, {
    componentId,
    pathLength: path.length,
    path,
  });

  return path;
}

/**
 * Get the full hierarchy path with component names
 *
 * @param componentId - ID of the component
 * @param allComponents - Array of all components
 * @returns Array of component names from root to target
 *
 * @example
 * ```typescript
 * const namePath = getComponentPathNames('button-1', components);
 * // Returns: ['Home Page', 'Hero Section', 'CTA Container', 'Submit Button']
 * ```
 */
export function getComponentPathNames(
  componentId: string,
  allComponents: Component[]
): string[] {
  const path = getComponentPath(componentId, allComponents);

  return path.map((id) => {
    const component = allComponents.find((c) => c.id === id);
    return component?.name || component?.type || id;
  });
}

/**
 * Get the parent component of a given component
 *
 * @param componentId - ID of the component
 * @param allComponents - Array of all components
 * @returns Parent component or undefined if root
 *
 * @example
 * ```typescript
 * const parent = getComponentParent('button-1', components);
 * // Returns: { id: 'container-1', type: 'flex-container', ... }
 * ```
 */
export function getComponentParent(
  componentId: string,
  allComponents: Component[]
): Component | undefined {
  const component = allComponents.find((c) => c.id === componentId);

  if (!component || !component.parentId) {
    return undefined;
  }

  return allComponents.find((c) => c.id === component.parentId);
}

/**
 * Get all root components (components with no parent)
 *
 * @param allComponents - Array of all components
 * @returns Array of root components
 *
 * @example
 * ```typescript
 * const roots = getRootComponents(components);
 * // Returns: [page1, page2, page3]
 * ```
 */
export function getRootComponents(allComponents: Component[]): Component[] {
  return allComponents.filter((c) => !c.parentId);
}

/**
 * Check if a component is an ancestor of another component
 *
 * @param ancestorId - Potential ancestor component ID
 * @param descendantId - Potential descendant component ID
 * @param allComponents - Array of all components
 * @returns True if ancestorId is an ancestor of descendantId
 *
 * @example
 * ```typescript
 * const isAncestor = isComponentAncestor('container-1', 'button-1', components);
 * // Returns: true (if container-1 is a parent/grandparent/etc. of button-1)
 * ```
 */
export function isComponentAncestor(
  ancestorId: string,
  descendantId: string,
  allComponents: Component[]
): boolean {
  const path = getComponentPath(descendantId, allComponents);
  return path.includes(ancestorId);
}

/**
 * Get sibling components (components with the same parent)
 *
 * @param componentId - ID of the component
 * @param allComponents - Array of all components
 * @param includeSelf - Whether to include the component itself in results
 * @returns Array of sibling components
 *
 * @example
 * ```typescript
 * const siblings = getComponentSiblings('button-1', components);
 * // Returns: [button-2, button-3, image-1] (siblings with same parent)
 * ```
 */
export function getComponentSiblings(
  componentId: string,
  allComponents: Component[],
  includeSelf: boolean = false
): Component[] {
  const component = allComponents.find((c) => c.id === componentId);

  if (!component) {
    return [];
  }

  const siblings = allComponents.filter((c) => {
    if (!includeSelf && c.id === componentId) return false;
    return c.parentId === component.parentId;
  });

  devLog('LAYOUT', `Found component siblings`, {
    componentId,
    siblingCount: siblings.length,
    includedSelf: includeSelf,
  });

  return siblings;
}

/**
 * Calculate maximum depth in the entire component tree
 *
 * @param allComponents - Array of all components
 * @returns Maximum nesting depth found
 *
 * @example
 * ```typescript
 * const maxDepth = getMaxDepth(components);
 * // Returns: 5 (deepest component is 5 levels from root)
 * ```
 */
export function getMaxDepth(allComponents: Component[]): number {
  let maxDepth = 0;

  for (const component of allComponents) {
    const depth = getComponentDepth(component.id, allComponents);
    if (depth > maxDepth) {
      maxDepth = depth;
    }
  }

  devLog('LAYOUT', `Maximum depth calculated`, {
    maxDepth,
    totalComponents: allComponents.length,
  });

  return maxDepth;
}

/**
 * Build a hierarchy tree structure from flat component array
 * Useful for visualization and debugging
 *
 * @param allComponents - Array of all components
 * @returns Tree structure with nested children
 */
export interface HierarchyNode {
  component: Component;
  depth: number;
  children: HierarchyNode[];
}

export function buildHierarchyTree(allComponents: Component[]): HierarchyNode[] {
  const roots = getRootComponents(allComponents);

  function buildNode(component: Component): HierarchyNode {
    const children = getComponentChildren(component.id, allComponents);
    const depth = getComponentDepth(component.id, allComponents);

    return {
      component,
      depth,
      children: children.map(buildNode),
    };
  }

  const tree = roots.map(buildNode);

  devLog('LAYOUT', `Hierarchy tree built`, {
    rootCount: roots.length,
    totalComponents: allComponents.length,
  });

  return tree;
}
